import { FC, ReactNode, useMemo, useState } from 'react';
import { Button, Colors, FlexBox, Skeleton, useBreakpoint } from '@brg/ui';
import { PageControl, PageNumber } from './styles';
import { ReactComponent as BackSvg } from './assets/back.svg';
import { ReactComponent as NextSvg } from './assets/next.svg';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { Input } from "@brg/input";
import { RectangularButtonHollow } from "../buttons";
import { LayoutConst } from 'core';
import { AppTester } from 'supporting';


export interface PaginationLayoutProps {
    isLoading?: boolean;
    paging?: ReactNode;
    button?: ReactNode;
    hidden?: boolean;
    disableOffset?: boolean;
}

const PaginationRoot = styled(FlexBox)<{ hidden?: boolean, disableOffset?: boolean }>`
    margin-top: 64px !important;
    margin-bottom: ${({ disableOffset }) => disableOffset ? 0 : '104px' };
    flex-wrap: wrap;
    height: 32px;
    max-height: 32px;
    flex-flow: row;

    button {
        height: 32px !important;
        max-height: 32px;
        max-width: 171px;
        min-width: 101px;
        font-size: 13px;
        line-height: 16px;
    }

    .pagging_item {
        font-size: 13px;
        line-height: 16px;
    }

    visibility: ${p => p.hidden ? 'hidden' : 'visible'};
    
    @media (max-width: ${LayoutConst.screen.m}px) {
        justify-content: space-between;
    }
    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        justify-content: center;
        flex-flow: column;

        > div:first-child {
            margin-bottom: 16px;
        }
    }
    @media (max-width: ${LayoutConst.screen.sm}px) {
      margin-bottom: ${({ disableOffset }) => disableOffset ? '24px' : '70px' };
    }
`

/**
 * Layout компонент переключателя страниц
 */
export const PaginationLayout: FC<PaginationLayoutProps> = (props) => {
    const { isLoading, paging, button, hidden, disableOffset } = props;

    return (
        <PaginationRoot
            style={{ minWidth: 'calc(100% - 20px)' }}
            gy={46}
            width="100%"
            justify="space-between"
            align="center"
            hidden={hidden}
            disableOffset={disableOffset}
        >
            {!isLoading && button}
            {isLoading ? (
                <FlexBox gx={8}>
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                </FlexBox>
            ) : (
                paging
            )}
        </PaginationRoot>
    );
};

export const DotsContainer = styled(FlexBox)`
    justify-content: center;
    align-items: center;
    min-width: 32px;
    height: 32px;
    border-radius: 3px;
    color: ${Colors.geoWhite47};
    user-select: none;
`;

export const PageInputWrapper = styled(FlexBox)`
    position: relative;
    height: 32px !important;
    max-height: 32px !important;
    max-width: 65px;
    width: 65px;
    background-color: ${({ theme }) => theme.inputs.pagination.mainPagination.pageBg.bg};
    border-radius: 3px;
    margin-right: 8px;
`;

export const PageInput = styled(Input.Text)`
    height: 100%;
    background-color: transparent !important;
    outline-color: transparent;
    color: ${({ theme }) => theme.app.text};

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    input {
        text-align: center;
        border: none !important;
        background-color: transparent;
        outline: none;
        padding: 0 !important;
    }


    transition: all 0.2s;
    
    font-size: 13px;
    line-height: 16px;

    @media (max-width: ${LayoutConst.screen.m}px) {
        height: 28px !important;
        font-size: 13px;
        input {
            padding: 0 12px;
        }
    }
`;

const PaggingItemWrapper = styled(FlexBox)`
    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
    }
`;

const PageInputGroup = styled.div`
    display: flex;
    white-space: nowrap;
    align-items: center;
    color: ${({ theme }) => theme.text.label};
    &:not(:first-child) {
        margin-top: 0;
    }
`

const PaginationGoToButton = styled(RectangularButtonHollow)`
    color: #8E9192 !important;
    &:hover {
        color: #C5C6C7 !important;
    }
`

type ScrollTarget = string | number | Element;

export interface PaginationProps {
    isLoading?: boolean;
    page: number;
    pages: number;
    onPageChange?: (page: number, scrollTarget?: ScrollTarget) => void;
    isCanLoadMore?: boolean;
    loadMore?: () => void;
    showLoadMore?: boolean;
    hidden?: boolean;
    disableOffset?: boolean;
    scrollTarget?: ScrollTarget;
}

const clamp = (min: number, value: number, max: number) => {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
}

/**
 * Компонент пагинации
 *
 * @param props
 * @returns
 */
export const Pagination: FC<PaginationProps> = (props) => {
    const { isLoading, page, onPageChange, pages, isCanLoadMore, showLoadMore, loadMore, hidden, scrollTarget } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const backStep = isMobile ? 1 : 5;
    const forwardStep = isMobile ? 1 : 5;
    const [pageText, setPageText] = useState<number>(page);

    const pagesExtr = useMemo(() => {
        const startStep = page >= pages - backStep ? pages - (backStep + forwardStep + 1) : page - backStep;
        const endStep = page <= forwardStep ? (backStep + forwardStep + 1) : page + forwardStep;
        const start = clamp(1, page > 7? startStep + 1 : startStep, pages);
        const end = clamp(1, (page > 6 && page + backStep + 1 < pages)? endStep - 1: endStep, pages);
        return {
            startPage: start,
            endPage: end
        }
    }, [backStep, forwardStep, page, pages])

    const {startPage: start, endPage: end} = pagesExtr
    

    const pagesListAlt = useMemo(() => {
        const arr = Array<number>();
        
        for (let i = start; i <= end; i++) {
            arr.push(i);
        }

        return arr
    }, [start, end])

    const isCompetition = AppTester.isCompetition() || AppTester.isCompetitionEducation() || window.location.pathname.includes("competition")

    const onPageChangeCreate = (p: number) => () => {
        onPageChange?.(p, scrollTarget);
    };
    
    const showLast = pages - page > backStep;

    const handleTextChange = ({ target: { value } }: any) => {
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
            return;
        }
        if (numericValue < 1) {
            setPageText(0)
        }
        else if (numericValue > pages) {
            setPageText(pages)
        }
        else {
            setPageText(numericValue);
        }
    }

    const handleSubmit = (e: any) => {
        e.preventDefault();
        onPageChange?.(pageText);
    }

    // Отрисовка
    return (
        <PaginationLayout
            isLoading={isLoading}
            disableOffset={props.disableOffset}
            button={
                showLoadMore && isCanLoadMore ? (
                    <Button.GreyLarge onClick={loadMore} style={{ width: '100%', color: theme.paginationBtn.color }}>
                        {t('show_more')}
                    </Button.GreyLarge>
                ) : null
            }
            hidden={hidden}
            paging={
                pages > 1 && (
                  <>
                    <PaggingItemWrapper gx={8}>
                        {(pages > (isMobile? 3 : 10)) && <PageControl onClick={onPageChangeCreate(page === 1? 1 : page - 1)}>
                            <BackSvg />
                        </PageControl>}
                        {page > forwardStep + 1 && start > 1 && (
                          <>                            
                            <PageNumber onClick={onPageChangeCreate(1)} active={false}>
                                1
                            </PageNumber>
                          </>
                        )}
                        {page > forwardStep + 2 && start > 2 && <DotsContainer>
                            ...
                        </DotsContainer>}
                        {pagesListAlt.map((value, index) => (
                            <div key={`pi_${value}_ ${index}`} className='pagging_item'>
                                {value > 0 ? (
                                    <PageNumber onClick={onPageChangeCreate(value)} active={value === page}>
                                        {value}
                                    </PageNumber>
                                ) : null}
                            </div>
                        ))}
                        {showLast && (
                            <>
                                {page < pages - backStep - 1 && end < pages - 1 && (
                                    <DotsContainer>
                                        ...
                                    </DotsContainer>)
                                }
                                {page < pages - backStep && end < pages && (
                                    <PageNumber onClick={onPageChangeCreate(pages)} active={false}>
                                        {pages}
                                    </PageNumber>
                                )}
                            </>
                        )}
                        {(pages > (isMobile? 3 : 10)) && <PageControl onClick={onPageChangeCreate(page < pages? page + 1 : page)}>
                            <NextSvg />
                        </PageControl>}
                    </PaggingItemWrapper>
                      <PageInputGroup>
                          <PageInputWrapper>
                            <PageInput
                                onChange={handleTextChange}
                                value={pageText}
                            />
                          </PageInputWrapper>
                          <PaginationGoToButton
                            style={isCompetition ? {padding: "8px 16px", fontSize: 13 } : {}}
                            disabled={!pageText || pageText > pages}
                            onClick={handleSubmit}
                          >
                              {t('pagination.go_to_page')}
                          </PaginationGoToButton>
                      </PageInputGroup>
                  </>
                )
            }
        />
    );
};
import { FC, ReactNode, useMemo, useState } from 'react';
import { Button, Colors, FlexBox, Skeleton, useBreakpoint } from '@brg/ui';
import { PageControl, PageNumber } from './styles';
import { ReactComponent as BackSvg } from './assets/back.svg';
import { ReactComponent as NextSvg } from './assets/next.svg';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { Input } from "@brg/input";
import { RectangularButtonHollow } from "../buttons";
import { LayoutConst } from 'core';
import { AppTester } from 'supporting';


export interface PaginationLayoutProps {
    isLoading?: boolean;
    paging?: ReactNode;
    button?: ReactNode;
    hidden?: boolean;
    disableOffset?: boolean;
}

const PaginationRoot = styled(FlexBox)<{ hidden?: boolean, disableOffset?: boolean }>`
    margin-top: 64px !important;
    margin-bottom: ${({ disableOffset }) => disableOffset ? 0 : '104px' };
    flex-wrap: wrap;
    height: 32px;
    max-height: 32px;
    flex-flow: row;

    button {
        height: 32px !important;
        max-height: 32px;
        max-width: 171px;
        min-width: 101px;
        font-size: 13px;
        line-height: 16px;
    }

    .pagging_item {
        font-size: 13px;
        line-height: 16px;
    }

    visibility: ${p => p.hidden ? 'hidden' : 'visible'};
    
    @media (max-width: ${LayoutConst.screen.m}px) {
        justify-content: space-between;
    }
    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        justify-content: center;
        flex-flow: column;

        > div:first-child {
            margin-bottom: 16px;
        }
    }
    @media (max-width: ${LayoutConst.screen.sm}px) {
      margin-bottom: ${({ disableOffset }) => disableOffset ? '24px' : '70px' };
    }
`

/**
 * Layout компонент переключателя страниц
 */
export const PaginationLayout: FC<PaginationLayoutProps> = (props) => {
    const { isLoading, paging, button, hidden, disableOffset } = props;

    return (
        <PaginationRoot
            style={{ minWidth: 'calc(100% - 20px)' }}
            gy={46}
            width="100%"
            justify="space-between"
            align="center"
            hidden={hidden}
            disableOffset={disableOffset}
        >
            {!isLoading && button}
            {isLoading ? (
                <FlexBox gx={8}>
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                </FlexBox>
            ) : (
                paging
            )}
        </PaginationRoot>
    );
};

export const DotsContainer = styled(FlexBox)`
    justify-content: center;
    align-items: center;
    min-width: 32px;
    height: 32px;
    border-radius: 3px;
    color: ${Colors.geoWhite47};
    user-select: none;
`;

export const PageInputWrapper = styled(FlexBox)`
    position: relative;
    height: 32px !important;
    max-height: 32px !important;
    max-width: 65px;
    width: 65px;
    background-color: ${({ theme }) => theme.inputs.pagination.mainPagination.pageBg.bg};
    border-radius: 3px;
    margin-right: 8px;
`;

export const PageInput = styled(Input.Text)`
    height: 100%;
    background-color: transparent !important;
    outline-color: transparent;
    color: ${({ theme }) => theme.app.text};

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    input {
        text-align: center;
        border: none !important;
        background-color: transparent;
        outline: none;
        padding: 0 !important;
    }


    transition: all 0.2s;
    
    font-size: 13px;
    line-height: 16px;

    @media (max-width: ${LayoutConst.screen.m}px) {
        height: 28px !important;
        font-size: 13px;
        input {
            padding: 0 12px;
        }
    }
`;

const PaggingItemWrapper = styled(FlexBox)`
    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
    }
`;

const PageInputGroup = styled.div`
    display: flex;
    white-space: nowrap;
    align-items: center;
    color: ${({ theme }) => theme.text.label};
    &:not(:first-child) {
        margin-top: 0;
    }
`

const PaginationGoToButton = styled(RectangularButtonHollow)`
    color: #8E9192 !important;
    &:hover {
        color: #C5C6C7 !important;
    }
`

type ScrollTarget = string | number | Element;

export interface PaginationProps {
    isLoading?: boolean;
    page: number;
    pages: number;
    onPageChange?: (page: number, scrollTarget?: ScrollTarget) => void;
    isCanLoadMore?: boolean;
    loadMore?: () => void;
    showLoadMore?: boolean;
    hidden?: boolean;
    disableOffset?: boolean;
    scrollTarget?: ScrollTarget;
}

const clamp = (min: number, value: number, max: number) => {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
}

/**
 * Компонент пагинации
 *
 * @param props
 * @returns
 */
export const Pagination: FC<PaginationProps> = (props) => {
    const { isLoading, page, onPageChange, pages, isCanLoadMore, showLoadMore, loadMore, hidden, scrollTarget } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const backStep = isMobile ? 1 : 5;
    const forwardStep = isMobile ? 1 : 5;
    const [pageText, setPageText] = useState<number>(page);

    const pagesExtr = useMemo(() => {
        const startStep = page >= pages - backStep ? pages - (backStep + forwardStep + 1) : page - backStep;
        const endStep = page <= forwardStep ? (backStep + forwardStep + 1) : page + forwardStep;
        const start = clamp(1, page > 7? startStep + 1 : startStep, pages);
        const end = clamp(1, (page > 6 && page + backStep + 1 < pages)? endStep - 1: endStep, pages);
        return {
            startPage: start,
            endPage: end
        }
    }, [backStep, forwardStep, page, pages])

    const {startPage: start, endPage: end} = pagesExtr
    

    const pagesListAlt = useMemo(() => {
        const arr = Array<number>();
        
        for (let i = start; i <= end; i++) {
            arr.push(i);
        }

        return arr
    }, [start, end])

    const isCompetition = AppTester.isCompetition() || AppTester.isCompetitionEducation() || window.location.pathname.includes("competition")

    const onPageChangeCreate = (p: number) => () => {
        onPageChange?.(p, scrollTarget);
    };
    
    const showLast = pages - page > backStep;

    const handleTextChange = ({ target: { value } }: any) => {
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
            return;
        }
        if (numericValue < 1) {
            setPageText(0)
        }
        else if (numericValue > pages) {
            setPageText(pages)
        }
        else {
            setPageText(numericValue);
        }
    }

    const handleSubmit = (e: any) => {
        e.preventDefault();
        onPageChange?.(pageText);
    }

    // Отрисовка
    return (
        <PaginationLayout
            isLoading={isLoading}
            disableOffset={props.disableOffset}
            button={
                showLoadMore && isCanLoadMore ? (
                    <Button.GreyLarge onClick={loadMore} style={{ width: '100%', color: theme.paginationBtn.color }}>
                        {t('show_more')}
                    </Button.GreyLarge>
                ) : null
            }
            hidden={hidden}
            paging={
                pages > 1 && (
                  <>
                    <PaggingItemWrapper gx={8}>
                        {(pages > (isMobile? 3 : 10)) && <PageControl onClick={onPageChangeCreate(page === 1? 1 : page - 1)}>
                            <BackSvg />
                        </PageControl>}
                        {page > forwardStep + 1 && start > 1 && (
                          <>                            
                            <PageNumber onClick={onPageChangeCreate(1)} active={false}>
                                1
                            </PageNumber>
                          </>
                        )}
                        {page > forwardStep + 2 && start > 2 && <DotsContainer>
                            ...
                        </DotsContainer>}
                        {pagesListAlt.map((value, index) => (
                            <div key={`pi_${value}_ ${index}`} className='pagging_item'>
                                {value > 0 ? (
                                    <PageNumber onClick={onPageChangeCreate(value)} active={value === page}>
                                        {value}
                                    </PageNumber>
                                ) : null}
                            </div>
                        ))}
                        {showLast && (
                            <>
                                {page < pages - backStep - 1 && end < pages - 1 && (
                                    <DotsContainer>
                                        ...
                                    </DotsContainer>)
                                }
                                {page < pages - backStep && end < pages && (
                                    <PageNumber onClick={onPageChangeCreate(pages)} active={false}>
                                        {pages}
                                    </PageNumber>
                                )}
                            </>
                        )}
                        {(pages > (isMobile? 3 : 10)) && <PageControl onClick={onPageChangeCreate(page < pages? page + 1 : page)}>
                            <NextSvg />
                        </PageControl>}
                    </PaggingItemWrapper>
                      <PageInputGroup>
                          <PageInputWrapper>
                            <PageInput
                                onChange={handleTextChange}
                                value={pageText}
                            />
                          </PageInputWrapper>
                          <PaginationGoToButton
                            style={isCompetition ? {padding: "8px 16px", fontSize: 13 } : {}}
                            disabled={!pageText || pageText > pages}
                            onClick={handleSubmit}
                          >
                              {t('pagination.go_to_page')}
                          </PaginationGoToButton>
                      </PageInputGroup>
                  </>
                )
            }
        />
    );
};
